/*
 * Copyright (c) Nexign, JSC, 2019-2024.
 */
/**
 *
 * @author Vlaidslav Oshkanov
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Ilya Babanov
 * @author Viacheslav Smalenski
 * @version 1.29.0
 * @since 1.0.0
 */

import { memo, useState } from 'react';
import type { ReactElement } from 'react';

import debounce from 'lodash/debounce';
import invoke from 'lodash/invoke';
import isEqual from 'lodash/isEqual';

import { MultiSelect } from '@components/common';
import { EInputKind } from '@components/common/Input/Input';
import type { IInputProps } from '@components/common/Input/Input';
import type { IOptionItemProps } from '@components/common/Select/Select';
import type { ICommonTranslation } from '@modules/internationalization/types';

import type { ISendFieldSuggestPayload } from '../../types/tableTypes';

/**
 * @description Multiselect filter props
 * @interface IFilterMultiSelectProps
 * @property {string} closeButton - close button label
 * @property {IInputProps} input - toggle input props
 * @property {string} placeholder - placeholder
 * @property {string} searchInputPlaceholder - toggle input placeholder
 * @property {IOptionItemProps[]} selectOptions - select options
 * @property {IOptionItemProps[]} selectedOptions - selected options
 * @property {() => void} onChange - change method
 * @property {ICommonTranslation} translation - multiSelect locale
 */
export interface IFilterMultiSelectProps<Field> {
    field: Field;
    input?: IInputProps;
    metaFacets: Field[];
    onChange: (options: IOptionItemProps[] | undefined) => void;
    placeholder?: string;
    renderOptions?: (props: any) => ReactElement;
    searchInputPlaceholder?: string;
    selectedOptions?: IOptionItemProps[];
    selectOptions?: IOptionItemProps[];
    sendFieldSuggest?: (payload: ISendFieldSuggestPayload) => void;
    suggestIsLoading?: boolean;
    translation: ICommonTranslation;
}

/**
 * @description Filter with multiselect.
 * @class FilterMultiSelect
 * @extends PureComponent<IFilterMultiSelectProps>
 *
 * @see BlockWithIcon component
 * @see RangeDatePicker component
 * @see TextBlock component
 */
function FilterMultiSelect<Field>(props: IFilterMultiSelectProps<Field>) {
    const [search, setSearch] = useState('');
    const isSuggest = !props.metaFacets.includes(props.field);

    const onSelect = (item: IOptionItemProps[]) => {
        if (!isEqual(item, props.selectedOptions)) {
            props.onChange(item);
        }
        onCloseHandler();
    };
    const onSearchHandler = (value: string) => {
        setSearch(value);
        if (isSuggest) {
            invoke(props, 'sendFieldSuggest', {
                field: props.field,
                value,
            });
        }
    };
    const onCloseHandler = () => {
        setSearch('');
        if (isSuggest) {
            invoke(props, 'sendFieldSuggest', {
                field: props.field,
                value: '',
            });
        }
    };

    const options =
        !search && !props.selectOptions?.length && props.selectedOptions ? props.selectedOptions : props.selectOptions;

    return (
        <MultiSelect
            input={props.input}
            isSuggest={isSuggest}
            kind={EInputKind.normal}
            options={options}
            placeholder={props.placeholder}
            renderOptions={props.renderOptions}
            searchInputPlaceholder={props.searchInputPlaceholder}
            selectedOptions={props.selectedOptions ? props.selectedOptions : []}
            suggestIsLoading={props.suggestIsLoading}
            translation={props.translation}
            onClose={onCloseHandler}
            onSearchInputChange={debounce(onSearchHandler, 500)}
            onSelect={onSelect}
        />
    );
}

FilterMultiSelect.defaultProps = {
    onChange: () => {
        /** */
    },
    searchInputPlaceholder: '',
};

export default memo(FilterMultiSelect);
